/* eslint-disable perfectionist/sort-imports */
import type { IconAliases } from 'vuetify';

import { createVuetify } from 'vuetify';
// Vuetify styles must be imported before any components that use it to make the css layers work correctly
import 'vuetify/styles';

import { VAvatar } from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { aliases, fa } from 'vuetify/iconsets/fa';

import { colors } from '@intake24/common/theme';

import resources from '../router/resources';

const resourceIcons = resources.reduce<Partial<IconAliases>>((acc, resource) => {
  acc[resource.name] = resource.icon;
  return acc;
}, {});

export default createVuetify({
  directives,
  aliases: {
    VDragAndDropHandle: VAvatar,
  },
  defaults: {
    global: {
      density: 'comfortable',
    },
    VAlert: {
      variant: 'tonal',
    },
    VBtn: {
      density: 'default',
      class: 'text-uppercase',
      variant: 'flat',
    },
    VCardActions: {
      VBtn: {
        variant: 'flat',
      },
    },
    VCombobox: {
      hideDetails: 'auto',
      variant: 'outlined',
    },
    VDragAndDropHandle: {
      class: ['drag-and-drop__handle'],
      icon: '$handle',
      variant: 'plain',
    },
    VDataTableServer: {
      VBtn: {
        icon: true,
        variant: 'text',
        size: 'small',
      },
    },
    VFileInput: {
      hideDetails: 'auto',
      variant: 'outlined',
    },
    VListItemAction: {
      VBtn: {
        variant: 'text',
        size: 'small',
      },
    },
    VSelect: {
      hideDetails: 'auto',
      variant: 'outlined',
    },
    VCheckbox: {
      hideDetails: 'auto',
    },
    VCheckboxBtn: {
      hideDetails: 'auto',
    },
    VNumberInput: {
      hideDetails: 'auto',
      variant: 'outlined',
    },
    VOtpInput: {
      hideDetails: 'auto',
    },
    VSwitch: {
      hideDetails: 'auto',
      color: 'primary',
    },
    VTextField: {
      hideDetails: 'auto',
      variant: 'outlined',
    },
    VTextarea: {
      hideDetails: 'auto',
      variant: 'outlined',
    },
    VTimePicker: {
      ampmInTitle: false,
      color: 'primary',
      format: '24hr',
      fullWidth: true,
      title: '',
    },
    VToolbar: {
      VBtn: {
        variant: 'flat',
      },
    },
  },
  display: {
    mobileBreakpoint: 'md',
  },
  icons: {
    defaultSet: 'fa',
    aliases: {
      ...aliases,
      add: 'fas fa-plus',
      cancel: 'fas fa-circle-xmark',
      check: 'fas fa-check-circle',
      close: 'fas fa-circle-xmark',
      create: 'fas fa-plus',
      back: 'fas fa-arrow-left',
      dashboard: 'fas fa-tachometer-alt',
      docs: 'fas fa-circle-info',
      delete: 'fas fa-trash',
      description: 'fas fa-note-sticky',
      desktop: 'fas fa-desktop',
      download: 'fas fa-download',
      edit: 'fas fa-edit',
      handle: 'fas fa-grip-vertical',
      login: 'fas fa-sign-in-alt',
      logout: 'fas fa-sign-out-alt',
      mobile: 'fas fa-mobile-screen',
      options: 'fas fa-ellipsis-vertical',
      read: 'far fa-file',
      redirect: 'fas fa-up-right-from-square',
      search: 'fas fa-magnifying-glass',
      save: 'fas fa-save',
      sync: 'fas fa-sync',
      times: 'fas fa-times-circle',
      upload: 'fas fa-upload',
      user: 'fas fa-user-circle',
      // MFA Provider icons
      code: 'fas fa-key',
      duo: 'fas fa-mobile-screen-button',
      fido: 'fas fa-microchip',
      otp: 'fas fa-stopwatch',
      // Resource icons
      ...resourceIcons,
      // Sub-resource icons
      categories: 'fas fa-list',
      foods: 'fas fa-drumstick-bite',
      'standard-units': 'fas fa-cubes-stacked',
    },
    sets: {
      fa,
    },
  },
  theme: {
    themes: {
      light: {
        colors: {
          ...colors,
          background: '#f9f2ee',
          'surface-light': colors['primary-lighten-4'],
        },
      },
      dark: {
        dark: true,
        colors,
      },
    },
  },
});
